import React, { useRef, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import Button from '@components/common/Button';
import { useDismissable } from '@hooks/useDismissable';
import { chartLegendEntries, exportChartPng, exportWidgetCsv } from '@utils/widgetExport';
import { showToast } from '@utils/toast';
import type { WidgetData } from '@app-types';
import styles from './WidgetExportMenu.module.css';

interface WidgetExportMenuProps {
  title: string;
  /** What the widget shows; the menu waits until it is there. */
  data: WidgetData | undefined;
  /** The chart's SVG as drawn on the page, or null when there is none. */
  getChartSvg: () => SVGSVGElement | null;
  testIdPrefix: string;
}

/**
 * Export of one widget: its chart as a PNG made in the browser and its data as CSV. A widget with no chart (a table
 * or key numbers only) offers the CSV alone.
 */
const WidgetExportMenu: React.FC<WidgetExportMenuProps> = ({ title, data, getChartSvg, testIdPrefix }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useDismissable(containerRef, isOpen, () => setIsOpen(false));

  const hasChart = (data?.chart?.categories.length ?? 0) > 0;

  const exportPng = async () => {
    setIsOpen(false);
    const svg = getChartSvg();
    if (!svg || !data?.chart) {
      showToast.error('The chart is not ready to export yet.');
      return;
    }
    try {
      await exportChartPng(title, svg, chartLegendEntries(data.chart));
    } catch {
      showToast.error('The chart could not be exported as a PNG.');
    }
  };

  const exportCsv = () => {
    setIsOpen(false);
    if (data) {
      exportWidgetCsv(title, data);
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <Button
        variant="ghost"
        size="xs"
        onClick={() => setIsOpen(!isOpen)}
        disabled={!data}
        aria-label={`Export ${title}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        dataTestId={`${testIdPrefix}-export`}
      >
        <Download size={16} />
        <ChevronDown size={12} />
      </Button>

      {isOpen && (
        <div className={styles.menu} role="menu" data-test-id={`${testIdPrefix}-export-menu`}>
          {hasChart && (
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={exportPng}
              data-test-id={`${testIdPrefix}-export-png`}
            >
              Chart as PNG
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={exportCsv}
            data-test-id={`${testIdPrefix}-export-csv`}
          >
            Data as CSV
          </button>
        </div>
      )}
    </div>
  );
};

export { WidgetExportMenu };
